import React, { useEffect, useState, useMemo } from 'react';

import {
    Box,
    Button,
    FormControl,
    FormLabel,
    Select,
    Input,
    Grid,
    Flex,
    useColorModeValue,
    VStack,
    Text,
    useToast,
    useTheme,

    // Tambahkan ini
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    Checkbox,
} from '@chakra-ui/react';

import { MdClear } from 'react-icons/md';
import { FiSearch } from 'react-icons/fi';
import api from '../../auth/apiClient';
// ============================================================
// TYPES
// ============================================================

interface Company {
    id: number;
    name: string;
}

interface Estate {
    id: number;
    name: string;
    odoo_id: number;
    company_id?: number;
}

interface Division {
    id: number;
    name: string;
    estate_id: number;
    odoo_id: number;
}

interface Pemanen {
    id: number;
    name: string;
    division_id: number;
}

/**
 * Tipe perbandingan.
 *
 * comparisonType hanya memiliki 3 pilihan karena
 * label yang ditampilkan adalah:
 *
 * JEJAK ↔ SAKTI
 * JEJAK ↔ AI
 * SAKTI ↔ AI
 */
export type ComparisonType =
    | 'JEJAK_SAKTI'
    | 'JEJAK_AI'
    | 'SAKTI_AI';

/**
 * Status selisih
 */
export type DifferenceStatus =
    | 'ALL'
    | 'DIFFERENT'
    | 'SAME';

// ============================================================
// PROPS
// ============================================================

interface ComparisonFilterProps {
    onSearch: (
        estateId?: number,
        divisionId?: number,
        pemanenName?: string,
        date?: string,
        comparisonType?: ComparisonType,
        selectedDiscrepancies?: string[],
        differenceStatus?: DifferenceStatus
    ) => void;

    isLoading: boolean;
}

// ============================================================
// COMPONENT
// ============================================================

export const ComparisonFilter: React.FC<ComparisonFilterProps> = ({
    onSearch,
    isLoading,
}) => {
    const toast = useToast();
    const theme = useTheme();

    // ========================================================
    // FILTER STATES
    // ========================================================

    const [selectedDate, setSelectedDate] = useState<string>(
        new Date().toISOString().split('T')[0]
    );

    const [companies, setCompanies] = useState<Company[]>([]);
    const [estates, setEstates] = useState<Estate[]>([]);
    const [divisions, setDivisions] = useState<Division[]>([]);
    const [allDivisions, setAllDivisions] = useState<Division[]>([]);
    const [pemanens, setPemanens] = useState<Pemanen[]>([]);
    const [allPemanens, setAllPemanens] = useState<Pemanen[]>([]);

    const [selectedCompanyId, setSelectedCompanyId] = useState<
        number | undefined
    >(undefined);

    const [selectedEstateId, setSelectedEstateId] = useState<
        number | undefined
    >(undefined);

    const [selectedDivisionId, setSelectedDivisionId] = useState<
        number | undefined
    >(undefined);

    const [selectedPemanenName, setSelectedPemanenName] = useState<
        string | undefined
    >(undefined);

    const [loadingOptions, setLoadingOptions] = useState<boolean>(true);

    // ========================================================
    // COMPARISON TYPE
    // ========================================================
    //
    // Ini adalah state FILTER.
    //
    // User bisa mengubah dropdown:
    //
    // JEJAK_SAKTI -> JEJAK_AI
    //
    // tetapi ComparisonCard belum berubah.
    //
    // ComparisonCard baru menerima nilai baru setelah
    // handleSubmit() menjalankan onSearch().
    //
    // ========================================================

    const [comparisonType, setComparisonType] =
        useState<ComparisonType>('JEJAK_SAKTI');

    // ========================================================
    // DISCREPANCY
    // ========================================================

    const [selectedDiscrepancies, setSelectedDiscrepancies] =
        useState<string[]>([
            'bunch_qty',
            'ripe_qty',
            'unripe_qty',
            'rotten_qty',
            'loose_qty',
            'abnormal_01_qty',
            'abnormal_02_qty',
            'abnormal_03_qty',
            'abnormal_04_qty',
            'abnormal_05_qty',
        ]);

    // ========================================================
    // DIFFERENCE STATUS
    // ========================================================

    const [differenceStatus, setDifferenceStatus] =
        useState<DifferenceStatus>('ALL');

    // ========================================================
    // DISCREPANCY OPTIONS
    // ========================================================

    const discrepancyOptions = [
        {
            value: 'bunch_qty',
            label: 'Total Janjang',
        },
        {
            value: 'ripe_qty',
            label: 'Janjang Matang',
        },
        {
            value: 'unripe_qty',
            label: 'Janjang Mentah',
        },
        {
            value: 'rotten_qty',
            label: 'Janjang Busuk',
        },
        {
            value: 'loose_qty',
            label: 'Brondolan',
        },
        {
            value: 'abnormal_01_qty',
            label: 'TBS < 5 KG',
        },
        {
            value: 'abnormal_02_qty',
            label: 'Dimakan Tikus',
        },
        {
            value: 'abnormal_03_qty',
            label: 'Buah Batu',
        },
        {
            value: 'abnormal_04_qty',
            label: 'Parthenocarpy',
        },
        {
            value: 'abnormal_05_qty',
            label: 'Tangkai Panjang',
        },
    ];

    // ========================================================
    // FILTERED ESTATES
    // ========================================================

    const filteredEstates = useMemo(() => {
        if (!selectedCompanyId) {
            return estates;
        }

        return estates.filter(
            (estate) => estate.company_id === selectedCompanyId
        );
    }, [estates, selectedCompanyId]);

    // ========================================================
    // INPUT STYLES
    // ========================================================

    const inputStyles = {
        size: 'sm' as const,
        borderRadius: 'lg',
        borderColor: 'gray.200',

        _hover: {
            borderColor: 'gray.300',
        },

        _focus: {
            borderColor: theme.colors.blue[400],
            boxShadow: `0 0 0 1px ${theme.colors.blue[400]}`,
        },
    };

    // ========================================================
    // LOAD FILTER OPTIONS
    // ========================================================

    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const [
                    optionsResponse,
                    estatesDbResponse,
                ] = await Promise.all([
                    api.get('/inspeksi/options'),
                    api.get('/inspeksi/web/estate-db'),
                ]);

                const data = optionsResponse.data;
                const estatesDbData = estatesDbResponse.data;

                const fetchedCompanies =
                    data.companies || [];

                setCompanies(fetchedCompanies);

                setAllDivisions(
                    data.divisis || []
                );

                setAllPemanens(
                    data.pemanens2 || []
                );

                // ====================================================
                // MERGE ESTATE
                // ====================================================

                const mergedEstates: Estate[] = (
                    estatesDbData || []
                ).map((estateDb: any) => {
                    const optionsEstate =
                        (data.estates || []).find(
                            (est: any) =>
                                est.name === estateDb.name
                        );

                    return {
                        id: estateDb.id,
                        name: estateDb.name,
                        company_id:
                            estateDb.company_id,
                        odoo_id:
                            optionsEstate?.odoo_id ||
                            estateDb.id,
                    };
                });

                setEstates(mergedEstates);

                // ====================================================
                // DEFAULT COMPANY
                // ====================================================

                if (fetchedCompanies.length > 0) {
                    const defaultCompany =
                        fetchedCompanies[0];

                    setSelectedCompanyId(
                        defaultCompany.id
                    );

                    setSelectedEstateId(
                        undefined
                    );
                }
            } catch (error) {
                console.error(
                    'Error fetching options:',
                    error
                );

                toast({
                    title: 'Gagal memuat filter',
                    description:
                        'Terjadi kesalahan saat memuat pilihan filter dari server.',
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
            } finally {
                setLoadingOptions(false);
            }
        };

        fetchOptions();
    }, [toast]);

    // ========================================================
    // CASCADING DIVISION
    // ========================================================

    useEffect(() => {
        if (!selectedEstateId) {
            setDivisions([]);
            setSelectedDivisionId(undefined);

            return;
        }

        const selectedEstate = estates.find(
            (estate) =>
                estate.odoo_id === selectedEstateId
        );

        if (selectedEstate) {
            const filteredDivisions =
                allDivisions.filter(
                    (division: Division) =>
                        division.estate_id ===
                        selectedEstate.id
                );

            setDivisions(filteredDivisions);

            setSelectedDivisionId(undefined);
        }
    }, [
        selectedEstateId,
        estates,
        allDivisions,
    ]);

    // ========================================================
    // CASCADING PEMANEN
    // ========================================================

    useEffect(() => {
        if (!selectedDivisionId) {
            setPemanens([]);
            setSelectedPemanenName(undefined);

            return;
        }

        const selectedDivision =
            divisions.find(
                (division) =>
                    division.odoo_id ===
                    selectedDivisionId
            );

        if (selectedDivision) {
            const filteredPemanens =
                allPemanens.filter(
                    (pemanen: Pemanen) =>
                        pemanen.division_id ===
                        selectedDivision.odoo_id
                );

            setPemanens(filteredPemanens);

            setSelectedPemanenName(undefined);
        }
    }, [
        selectedDivisionId,
        divisions,
        allPemanens,
    ]);

    // ========================================================
    // SUBMIT
    // ========================================================
    //
    // PENTING:
    //
    // Jangan gunakan selectedComparisonType karena state
    // yang kita punya bernama comparisonType.
    //
    // Ketika tombol "Terapkan Filter" ditekan:
    //
    // comparisonType dikirim ke ComparisonPage.
    //
    // ComparisonPage kemudian menyimpan nilai tersebut.
    //
    // ComparisonCard menggunakan nilai dari ComparisonPage.
    //
    // ========================================================

    const handleSubmit = (
        e: React.FormEvent
    ) => {
        e.preventDefault();

        if (!selectedDate) {
            toast({
                title: 'Filter tidak lengkap',
                description:
                    'Silakan pilih Tanggal terlebih dahulu.',
                status: 'warning',
                duration: 3000,
                isClosable: true,
            });

            return;
        }

        onSearch(
            selectedEstateId,
            selectedDivisionId,
            selectedPemanenName,
            selectedDate,
            comparisonType,
            selectedDiscrepancies,
            differenceStatus
        );
    };

    // ========================================================
    // RESET
    // ========================================================

    const handleReset = () => {
        setSelectedDate(
            new Date()
                .toISOString()
                .split('T')[0]
        );

        if (companies.length > 0) {
            setSelectedCompanyId(
                companies[0].id
            );
        }

        const estate14 = estates.find(
            (estate) =>
                estate.odoo_id === 14
        );

        if (
            estate14 &&
            companies.length > 0 &&
            estate14.company_id ===
                companies[0].id
        ) {
            setSelectedEstateId(14);
        } else {
            setSelectedEstateId(undefined);
        }

        setSelectedDivisionId(undefined);
        setSelectedPemanenName(undefined);

        // Reset comparison
        setComparisonType('JEJAK_SAKTI');

        // Reset discrepancy
        setSelectedDiscrepancies(
            discrepancyOptions.map(
                (item) => item.value
            )
        );

        // Reset status
        setDifferenceStatus('ALL');
    };

    // ========================================================
    // COMPANY CHANGE
    // ========================================================

    const handleCompanyChange = (
        e: React.ChangeEvent<HTMLSelectElement>
    ) => {
        const val = e.target.value;

        setSelectedCompanyId(
            val ? Number(val) : undefined
        );

        setSelectedEstateId(undefined);
        setSelectedDivisionId(undefined);
        setSelectedPemanenName(undefined);
    };

    // ========================================================
    // ESTATE CHANGE
    // ========================================================

    const handleEstateChange = (
        e: React.ChangeEvent<HTMLSelectElement>
    ) => {
        const val = e.target.value;

        setSelectedEstateId(
            val ? Number(val) : undefined
        );

        setSelectedDivisionId(undefined);
        setSelectedPemanenName(undefined);
    };

    // ========================================================
    // DIVISION CHANGE
    // ========================================================

    const handleDivisionChange = (
        e: React.ChangeEvent<HTMLSelectElement>
    ) => {
        const val = e.target.value;

        setSelectedDivisionId(
            val ? Number(val) : undefined
        );

        setSelectedPemanenName(undefined);
    };

    // ========================================================
    // PEMANEN CHANGE
    // ========================================================

    const handlePemanenChange = (
        e: React.ChangeEvent<HTMLSelectElement>
    ) => {
        const val = e.target.value;

        setSelectedPemanenName(
            val ? String(val) : undefined
        );
    };

    // ========================================================
    // DISCREPANCY CHANGE
    // ========================================================

    const handleDiscrepancyChange = (
        value: string
    ) => {
        setSelectedDiscrepancies(
            (prev) => {
                if (prev.includes(value)) {
                    return prev.filter(
                        (item) =>
                            item !== value
                    );
                }

                return [
                    ...prev,
                    value,
                ];
            }
        );
    };

    // ========================================================
    // SELECT ALL
    // ========================================================

    const handleSelectAll = () => {
        setSelectedDiscrepancies(
            (prev) => {
                if (
                    prev.length ===
                    discrepancyOptions.length
                ) {
                    return [];
                }

                return discrepancyOptions.map(
                    (item) => item.value
                );
            }
        );
    };

    // ========================================================
    // COLORS
    // ========================================================

    const bgColor =
        useColorModeValue(
            'white',
            'gray.800'
        );

    const borderColor =
        useColorModeValue(
            'gray.100',
            'gray.700'
        );

    const selectTextColor =
        useColorModeValue(
            'gray.700',
            'white'
        );

    const menuBg =
        useColorModeValue(
            'white',
            'gray.700'
        );

    // ========================================================
    // RENDER
    // ========================================================

    return (
        <Box
            as="form"
            onSubmit={handleSubmit}
            bg={bgColor}
            border="1px"
            borderColor={borderColor}
            borderRadius="xl"
            p={{ base: 4, md: 6 }}
            boxShadow="sm"
            mb={6}
        >
            <VStack
                spacing={4}
                align="stretch"
            >

                {/* ==================================================
                    FILTER UTAMA
                ================================================== */}

                <Grid
                    templateColumns={{
                        base: '1fr',
                        md: 'repeat(2, 1fr)',
                        lg: 'repeat(5, 1fr)',
                    }}
                    gap={4}
                >

                    {/* DATE */}

                    <FormControl isRequired>
                        <FormLabel
                            fontSize="sm"
                            color="gray.600"
                            mb={1}
                        >
                            Tanggal
                        </FormLabel>

                        <Input
                            {...inputStyles}
                            type="date"
                            value={selectedDate}
                            onChange={(e) =>
                                setSelectedDate(
                                    e.target.value
                                )
                            }
                        />
                    </FormControl>

                    {/* COMPANY */}

                    <FormControl
                        isDisabled={
                            loadingOptions
                        }
                    >
                        <FormLabel
                            fontSize="sm"
                            color="gray.600"
                            mb={1}
                        >
                            Company
                        </FormLabel>

                        <Select
                            {...inputStyles}
                            value={
                                selectedCompanyId ||
                                ''
                            }
                            onChange={
                                handleCompanyChange
                            }
                        >
                            {companies.map(
                                (company) => (
                                    <option
                                        key={
                                            company.id
                                        }
                                        value={
                                            company.id
                                        }
                                    >
                                        {
                                            company.name
                                        }
                                    </option>
                                )
                            )}
                        </Select>
                    </FormControl>

                    {/* ESTATE */}

                    <FormControl
                        isDisabled={
                            loadingOptions
                        }
                    >
                        <FormLabel
                            fontSize="sm"
                            color="gray.600"
                            mb={1}
                        >
                            Estate
                        </FormLabel>

                        <Select
                            {...inputStyles}
                            placeholder="Semua Estate"
                            value={
                                selectedEstateId ||
                                ''
                            }
                            onChange={
                                handleEstateChange
                            }
                        >
                            {filteredEstates.map(
                                (estate) => (
                                    <option
                                        key={
                                            estate.id
                                        }
                                        value={
                                            estate.odoo_id
                                        }
                                    >
                                        {
                                            estate.name
                                        }
                                    </option>
                                )
                            )}
                        </Select>
                    </FormControl>

                    {/* DIVISION */}

                    <FormControl
                        isDisabled={
                            loadingOptions ||
                            !selectedEstateId ||
                            divisions.length ===
                                0
                        }
                    >
                        <FormLabel
                            fontSize="sm"
                            color="gray.600"
                            mb={1}
                        >
                            Divisi
                        </FormLabel>

                        <Select
                            {...inputStyles}
                            placeholder="Semua Divisi"
                            value={
                                selectedDivisionId ||
                                ''
                            }
                            onChange={
                                handleDivisionChange
                            }
                        >
                            {divisions.map(
                                (division) => (
                                    <option
                                        key={
                                            division.id
                                        }
                                        value={
                                            division.odoo_id
                                        }
                                    >
                                        {
                                            division.name
                                        }
                                    </option>
                                )
                            )}
                        </Select>
                    </FormControl>

                    {/* PEMANEN */}

                    <FormControl
                        isDisabled={
                            loadingOptions ||
                            !selectedDivisionId ||
                            pemanens.length ===
                                0
                        }
                    >
                        <FormLabel
                            fontSize="sm"
                            color="gray.600"
                            mb={1}
                        >
                            Pemanen
                        </FormLabel>

                        <Select
                            {...inputStyles}
                            placeholder="Semua Pemanen"
                            value={
                                selectedPemanenName ||
                                ''
                            }
                            onChange={
                                handlePemanenChange
                            }
                        >
                            {pemanens.map(
                                (pemanen) => (
                                    <option
                                        key={
                                            pemanen.id
                                        }
                                        value={
                                            pemanen.name
                                        }
                                    >
                                        {
                                            pemanen.name
                                        }
                                    </option>
                                )
                            )}
                        </Select>
                    </FormControl>
                </Grid>

                {/* ==================================================
                    COMPARISON / DISCREPANCY / STATUS
                ================================================== */}

                <Grid
                    templateColumns={{
                        base: '1fr',
                        md: 'repeat(2, 1fr)',
                        lg: 'repeat(3, 1fr)',
                    }}
                    gap={4}
                >

                    {/* ==================================================
                        COMPARISON TYPE
                    ================================================== */}

                    <FormControl>
                        <FormLabel
                            fontSize="sm"
                            color="gray.600"
                            mb={1}
                        >
                            Bandingkan
                        </FormLabel>

                        <Select
                            {...inputStyles}
                            value={comparisonType}
                            onChange={(e) => {
                                const value =
                                    e.target
                                        .value as ComparisonType;

                                setComparisonType(
                                    value
                                );
                            }}
                        >
                            <option value="JEJAK_SAKTI">
                                JEJAK ↔ SAKTI
                            </option>

                            <option value="JEJAK_AI">
                                JEJAK ↔ AI
                            </option>

                            <option value="SAKTI_AI">
                                SAKTI ↔ AI
                            </option>
                        </Select>
                    </FormControl>

                    {/* ==================================================
                        DISCREPANCY
                    ================================================== */}

                    <FormControl>
                        <FormLabel
                            fontSize="sm"
                            color="gray.600"
                            mb={1}
                        >
                            Jenis Selisih
                        </FormLabel>

                        <Menu
                            closeOnSelect={false}
                        >
                            <MenuButton
                                as={Button}
                                {...inputStyles}
                                width="100%"
                                textAlign="left"
                                fontWeight="normal"
                                color={
                                    selectTextColor
                                }
                                bg={menuBg}
                                borderWidth="1px"
                                borderStyle="solid"
                                borderColor={useColorModeValue(
                                    'gray.200',
                                    'gray.600'
                                )}
                                boxShadow="none"
                                _hover={{
                                    bg: useColorModeValue(
                                        'gray.50',
                                        'gray.600'
                                    ),
                                    borderColor:
                                        useColorModeValue(
                                            'gray.300',
                                            'gray.500'
                                        ),
                                }}
                                _focus={{
                                    borderColor:
                                        useColorModeValue(
                                            'gray.300',
                                            'gray.500'
                                        ),
                                    boxShadow:
                                        'none',
                                }}
                            >
                                {selectedDiscrepancies.length ===
                                0
                                    ? 'Pilih Jenis Selisih'
                                    : selectedDiscrepancies.length ===
                                      discrepancyOptions.length
                                    ? 'Semua Jenis Selisih'
                                    : `${selectedDiscrepancies.length} Jenis Dipilih`}
                            </MenuButton>

                            <MenuList minWidth="280px">

                                {/* SELECT ALL */}

                                <MenuItem
                                    onClick={
                                        handleSelectAll
                                    }
                                    closeOnSelect={
                                        false
                                    }
                                >
                                    <Checkbox
                                        isChecked={
                                            selectedDiscrepancies.length ===
                                            discrepancyOptions.length
                                        }
                                        pointerEvents="none"
                                    >
                                        Pilih Semua
                                    </Checkbox>
                                </MenuItem>

                                <Box
                                    borderTop="1px solid"
                                    borderColor="gray.200"
                                    my={1}
                                />

                                {/* OPTIONS */}

                                {discrepancyOptions.map(
                                    (item) => (
                                        <MenuItem
                                            key={
                                                item.value
                                            }
                                            onClick={() =>
                                                handleDiscrepancyChange(
                                                    item.value
                                                )
                                            }
                                            closeOnSelect={
                                                false
                                            }
                                        >
                                            <Checkbox
                                                isChecked={selectedDiscrepancies.includes(
                                                    item.value
                                                )}
                                                pointerEvents="none"
                                            >
                                                {
                                                    item.label
                                                }
                                            </Checkbox>
                                        </MenuItem>
                                    )
                                )}
                            </MenuList>
                        </Menu>
                    </FormControl>

                    {/* ==================================================
                        DIFFERENCE STATUS
                    ================================================== */}

                    <FormControl>
                        <FormLabel
                            fontSize="sm"
                            color="gray.600"
                            mb={1}
                        >
                            Status Selisih
                        </FormLabel>

                        <Select
                            {...inputStyles}
                            value={
                                differenceStatus
                            }
                            onChange={(e) => {
                                const value =
                                    e.target.value as DifferenceStatus;

                                setDifferenceStatus(value);

                                // Otomatis pilih semua jenis selisih
                                setSelectedDiscrepancies(
                                    discrepancyOptions.map((item) => item.value)
                                );

                                setDifferenceStatus(
                                    value
                                );
                            }}
                        >
                            <option value="ALL">
                                Semua
                            </option>

                            <option value="DIFFERENT">
                                Ada Selisih
                            </option>

                            <option value="SAME">
                                Tidak Ada Selisih
                            </option>
                        </Select>
                    </FormControl>
                </Grid>

                {/* ==================================================
                    BUTTONS
                ================================================== */}

                <Flex
                    gap={2}
                    justify={{
                        base: 'stretch',
                        md: 'flex-end',
                    }}
                    direction={{
                        base: 'column',
                        sm: 'row',
                    }}
                >

                    {/* RESET */}

                    <Button
                        leftIcon={
                            <MdClear />
                        }
                        onClick={
                            handleReset
                        }
                        isDisabled={
                            isLoading ||
                            loadingOptions
                        }
                        size="sm"
                        variant="outline"
                        colorScheme="red"
                        borderRadius="lg"
                        flex={{
                            base: 1,
                            sm: 'initial',
                        }}
                    >
                        Reset
                    </Button>

                    {/* SEARCH */}

                    <Button
                        leftIcon={
                            <FiSearch />
                        }
                        colorScheme="blue"
                        type="submit"
                        isLoading={
                            isLoading
                        }
                        isDisabled={
                            loadingOptions
                        }
                        size="sm"
                        borderRadius="lg"
                        flex={{
                            base: 1,
                            sm: 'initial',
                        }}
                    >
                        Terapkan Filter
                    </Button>
                </Flex>
            </VStack>
        </Box>
    );
};
